import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const initialState = {
  list: [],
  getAdminLoading: false,
  getAdminError: null,
  createAdminLoading: false,
  createAdminError: null,
  updateAdminLoading: false,
  updateAdminError: null,
  viewAdminLoading: false,
  viewAdminError: null,
  selectedAdmin: null,
  deleteAdminLoading: false,
  deleteAdminError: null,
};

export const fetchAdmins = createAsyncThunk(
  "admin/fetchAdmins",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("OrviSlideToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/admins`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to fetch admins",
        );
      }
      const data = await response.json();
      const adminData = data.data;
      return adminData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createAdmin = createAsyncThunk(
  "admin/addAdmin",
  async (adminData, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/admins`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: adminData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateAdmin = createAsyncThunk(
  "admin/updateAdmin",
  async (adminData, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/admins/${adminData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(adminData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const viewAdmin = createAsyncThunk(
  "admin/viewAdmin",
  async (adminId, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/admins/${adminId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to view admin",
        );
      }
      const data = await response.json();
      const adminData = data.data;
      return adminData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteAdmin = createAsyncThunk(
  "admin/deleteAdmin",
  async (adminId, { rejectWithValue }) => {
    const token = localStorage.getItem("OrviSlideToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/admins/${adminId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(
          errorData.message
            ? errorData
            : errorData.error || "Failed to delete admin",
        );
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);
const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchAdmins.pending, (state) => {
        state.getAdminLoading = true;
        state.getAdminError = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.getAdminLoading = false;
        state.list = action.payload;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.getAdminLoading = false;
        state.getAdminError = action.payload;
      })

      // create
      .addCase(createAdmin.pending, (state) => {
        state.createAdminLoading = true;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.createAdminLoading = false;
        state.list.push(action.payload);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.createAdminLoading = false;
        state.createAdminError = action.payload;
      })

      // ✅ Update
      .addCase(updateAdmin.pending, (state) => {
        state.updateAdminLoading = true;
        state.updateAdminError = null;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.updateAdminLoading = false;
        const updatedAdmin = action.payload;
        state.list = state.list.map((admin) =>
          admin.id === updatedAdmin.id ? updatedAdmin : admin,
        );
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.updateAdminLoading = false;
        state.updateAdminError = action.payload;
      })

      // ✅ View single admin
      .addCase(viewAdmin.pending, (state) => {
        state.viewAdminLoading = true;
        state.viewAdminError = null;
        state.selectedAdmin = null;
      })
      .addCase(viewAdmin.fulfilled, (state, action) => {
        state.viewAdminLoading = false;
        state.selectedAdmin = action.payload;
      })
      .addCase(viewAdmin.rejected, (state, action) => {
        state.viewAdminLoading = false;
        state.viewAdminError = action.payload;
      })

      // ✅ Delete Admin
      .addCase(deleteAdmin.pending, (state) => {
        state.deleteAdminLoading = true;
        state.deleteAdminError = null;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        state.deleteAdminLoading = false;
        const deletedId = action.payload.id;
        state.list = state.list.filter((admin) => admin.id !== deletedId);
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.deleteAdminLoading = false;
        state.deleteAdminError = action.payload;
      });
  },
});

export default adminSlice.reducer;
